import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ScheduledEntry } from 'src/app/_interfaces';
import { GolfTournament } from 'src/app/_interfaces/golf/golf-tournament';
import { GolfService } from 'src/app/_services/golf.service.service';
import { SsrlocalstoreService } from 'src/app/_services/ssr-localstore.service';
import { GolfTournamentSocketService } from 'src/app/_sockets/golf-tournament-socket.service';


@Injectable({ providedIn: 'root' })
export class ScheduledEntryStateService implements OnDestroy {
    public entry = new BehaviorSubject<ScheduledEntry>({} as ScheduledEntry);
    public tournament = new BehaviorSubject<GolfTournament | null>(null);
    private teamId: string | null = null;
    private sub?: Subscription;
    public deltaTick = new BehaviorSubject<number>(0);
    private deltaSub?: Subscription;

    constructor(
        private golfSocket: GolfTournamentSocketService,
        private ssr: SsrlocalstoreService,
        private golfService: GolfService
    ) { }

    /**
     * Initialize live tournament stream for a team_id.
     * - joins `golf_tournament:<team_id>`
     * - listens for `golf_delta` merged updates via tournament$(team_id)
     */
    async init(team_id: string): Promise<void> {
        if (!team_id) return;

        // already initialized for this team
        if (this.teamId === team_id && (this.sub || !this.ssr.isClientSide)) return;

        // switching teams or first init
        this.cleanup();
        this.teamId = team_id;

        const prefetched = await this.golfService.getTournamentsByTeamID(team_id);
        if (prefetched?.length) {
            this.tournament.next(prefetched[0]);
        }

        if (!this.ssr.isClientSide) {
            return;
        }

        try {
            const initial = await this.golfSocket.joinTournamentChannel(team_id);

            // If join succeeds, prefer socket's "authoritative" initial payload
            if (initial?.id) {
                this.tournament.next(initial as unknown as GolfTournament);
            }

            // subscribe to live updates
            this.sub = this.golfSocket.tournament$(team_id).subscribe((t) => {
                if (!t) return;
                this.tournament.next(t as unknown as GolfTournament);
            });

            this.deltaSub = this.golfSocket.deltaTick$(team_id).subscribe((tick) => {
                this.deltaTick.next(tick);
            });
        } catch (e) {
            console.warn('[ScheduledEntryState] golf socket join failed; keeping HTTP tournament', e);
            return;
        }
    }

    setEntry(entry: ScheduledEntry): void {
        this.entry.next(entry);
    }

    cleanup(): void {
        this.sub?.unsubscribe();
        this.sub = undefined;

        this.deltaSub?.unsubscribe();
        this.deltaSub = undefined;

        this.deltaTick.next(0);

        if (this.ssr.isClientSide && this.teamId) {
            this.golfSocket.disconnect(this.teamId);
        }

        this.teamId = null;
    }

    ngOnDestroy(): void {
        this.cleanup();
    }
}
